package br.com.vvrefeicoes.portal.core.util.spreadsheet

import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.plus
import kotlin.math.abs
import kotlin.math.roundToLong

/**
 * Leitor mínimo de `.xls` (Excel 97-2003, BIFF8 dentro de um arquivo composto OLE2): extrai a
 * aba de planilha com mais células como uma grade de strings, resolvendo textos compartilhados,
 * números, fórmulas já calculadas e datas. Sem dependências externas.
 *
 * A aba escolhida é a maior porque esses arquivos costumam trazer abas auxiliares (tabelas
 * dinâmicas, resumos) antes dos dados. Linhas totalmente vazias são descartadas. Números com
 * casas decimais saem com vírgula ("2467,58"), o formato esperado pelas telas de importação.
 */
internal object XlsReader {
    private val MAGIC = intArrayOf(0xD0, 0xCF, 0x11, 0xE0, 0xA1, 0xB1, 0x1A, 0xE1)
    private val EXCEL_EPOCH = LocalDate(1899, 12, 30)
    private val BUILTIN_DATE_FORMATS = (14..22).toSet() + (27..36).toSet() + (45..47).toSet() + (50..58).toSet()
    private val QUOTED_OR_BRACKETED = Regex("\"[^\"]*\"|\\[[^\\]]*\\]|\\\\.")

    private const val DAYS_1904_OFFSET = 1462
    private const val MAX_EXCEL_SERIAL = 2_958_465L
    private const val DECIMAL_SCALE = 1_000_000L
    private const val DECIMAL_DIGITS = 6

    private const val BIFF8 = 0x0600
    private const val SHEET_TYPE_WORKSHEET = 0

    private const val RECORD_FORMULA = 0x0006
    private const val RECORD_EOF = 0x000A
    private const val RECORD_DATE_MODE = 0x0022
    private const val RECORD_FILE_PASS = 0x002F
    private const val RECORD_CONTINUE = 0x003C
    private const val RECORD_BOUND_SHEET = 0x0085
    private const val RECORD_MUL_RK = 0x00BD
    private const val RECORD_XF = 0x00E0
    private const val RECORD_SST = 0x00FC
    private const val RECORD_LABEL_SST = 0x00FD
    private const val RECORD_NUMBER = 0x0203
    private const val RECORD_LABEL = 0x0204
    private const val RECORD_BOOL_ERR = 0x0205
    private const val RECORD_STRING = 0x0207
    private const val RECORD_RK = 0x027E
    private const val RECORD_FORMAT = 0x041E
    private const val RECORD_BOF = 0x0809

    fun isXls(data: ByteArray): Boolean = data.size >= MAGIC.size && MAGIC.indices.all { (data[it].toInt() and 0xFF) == MAGIC[it] }

    fun read(data: ByteArray): List<List<String>> {
        val compound = CompoundFile(data)
        val workbook =
            compound.stream("Workbook")
                ?: compound.stream("Book")
                ?: throw SpreadsheetFormatException("Arquivo XLS sem pasta de trabalho")
        return Workbook(workbook).largestSheet()
    }

    /** Valor de uma célula RK: inteiro de 30 bits ou os 30 bits altos de um double, com divisão opcional por 100. */
    internal fun decodeRk(rk: Int): Double {
        val value =
            if (rk and 2 != 0) {
                (rk shr 2).toDouble()
            } else {
                Double.fromBits((rk.toLong() and 0xFFFFFFFCL) shl 32)
            }
        return if (rk and 1 != 0) value / 100 else value
    }

    /** Inteiros sem casas; os demais com até seis casas e vírgula decimal, sem separador de milhar. */
    internal fun formatNumber(value: Double): String {
        if (value.isNaN() || value.isInfinite()) return ""
        val scaled = (abs(value) * DECIMAL_SCALE).roundToLong()
        val sign = if (value < 0 && scaled != 0L) "-" else ""
        val integer = scaled / DECIMAL_SCALE
        val fraction = (scaled % DECIMAL_SCALE).toString().padStart(DECIMAL_DIGITS, '0').trimEnd('0')
        return if (fraction.isEmpty()) "$sign$integer" else "$sign$integer,$fraction"
    }

    /** Um código de formato é de data quando, fora de textos e colchetes, tem dia ou ano. */
    internal fun isDateFormat(code: String): Boolean {
        val bare = QUOTED_OR_BRACKETED.replace(code.lowercase(), "")
        return (bare.contains('d') || bare.contains('y')) && !bare.contains('%')
    }

    private fun formatDate(
        serial: Double,
        date1904: Boolean,
    ): String? {
        val days = serial.toLong() + if (date1904) DAYS_1904_OFFSET else 0
        if (days < 0 || days > MAX_EXCEL_SERIAL) return null
        val date = EXCEL_EPOCH.plus(days.toInt(), DateTimeUnit.DAY)
        return "${pad2(date.day)}/${pad2(date.month.ordinal + 1)}/${date.year}"
    }

    private fun pad2(value: Int): String = if (value < 10) "0$value" else value.toString()

    private fun u8(
        data: ByteArray,
        offset: Int,
    ): Int = data[offset].toInt() and 0xFF

    private fun u16(
        data: ByteArray,
        offset: Int,
    ): Int = u8(data, offset) or (u8(data, offset + 1) shl 8)

    private fun i32(
        data: ByteArray,
        offset: Int,
    ): Int = u16(data, offset) or (u16(data, offset + 2) shl 16)

    private fun f64(
        data: ByteArray,
        offset: Int,
    ): Double = Double.fromBits((i32(data, offset).toLong() and 0xFFFFFFFFL) or (i32(data, offset + 4).toLong() shl 32))

    /** Arquivo composto OLE2: setores encadeados por uma tabela de alocação (FAT) e um diretório de fluxos. */
    private class CompoundFile(
        private val data: ByteArray,
    ) {
        private val sectorSize: Int
        private val miniSectorSize: Int
        private val miniCutoff: Int
        private val fat: IntArray
        private val miniFat: IntArray
        private val entries: List<Entry>

        private class Entry(
            val name: String,
            val type: Int,
            val start: Int,
            val size: Int,
        )

        init {
            if (!isXls(data) || data.size < HEADER_SIZE) throw SpreadsheetFormatException("Arquivo XLS inválido")
            sectorSize = 1 shl u16(data, 0x1E)
            miniSectorSize = 1 shl u16(data, 0x20)
            miniCutoff = i32(data, 0x38)
            fat = readFat()
            entries = readDirectory(i32(data, 0x30))
            miniFat = readInts(chain(i32(data, 0x3C)))
        }

        fun stream(name: String): ByteArray? {
            val entry = entries.firstOrNull { it.type == TYPE_STREAM && it.name.equals(name, ignoreCase = true) } ?: return null
            if (entry.size >= miniCutoff) return readSectors(chain(entry.start), entry.size)
            // Fluxos pequenos ficam no "mini fluxo", guardado dentro do fluxo da entrada raiz.
            val root = entries.firstOrNull { it.type == TYPE_ROOT } ?: return null
            val container = readSectors(chain(root.start), root.size)
            val result = ByteArray(entry.size)
            var sector = entry.start
            var written = 0
            var guard = 0
            while (sector >= 0 && written < entry.size && guard++ <= miniFat.size) {
                val offset = sector * miniSectorSize
                val length = minOf(miniSectorSize, entry.size - written)
                if (offset + length > container.size) break
                container.copyInto(result, written, offset, offset + length)
                written += length
                sector = miniFat.getOrElse(sector) { END_OF_CHAIN }
            }
            return result
        }

        private fun sectorOffset(sector: Int): Int = (sector + 1) * sectorSize

        private fun readFat(): IntArray {
            val fatSectors = ArrayList<Int>()
            for (index in 0 until HEADER_DIFAT_ENTRIES) {
                val sector = i32(data, 0x4C + index * 4)
                if (sector >= 0) fatSectors.add(sector)
            }
            // Arquivos grandes continuam a lista de setores da FAT em setores DIFAT encadeados.
            var difat = i32(data, 0x44)
            var guard = 0
            val perSector = sectorSize / 4 - 1
            while (difat >= 0 && guard++ < data.size / sectorSize) {
                val offset = sectorOffset(difat)
                if (offset + sectorSize > data.size) break
                for (index in 0 until perSector) {
                    val sector = i32(data, offset + index * 4)
                    if (sector >= 0) fatSectors.add(sector)
                }
                difat = i32(data, offset + perSector * 4)
            }
            return readInts(fatSectors)
        }

        private fun readInts(sectors: List<Int>): IntArray {
            val perSector = sectorSize / 4
            val table = IntArray(sectors.size * perSector) { FREE_SECTOR }
            sectors.forEachIndexed { position, sector ->
                val offset = sectorOffset(sector)
                if (offset + sectorSize <= data.size) {
                    for (index in 0 until perSector) table[position * perSector + index] = i32(data, offset + index * 4)
                }
            }
            return table
        }

        private fun chain(start: Int): List<Int> {
            val sectors = ArrayList<Int>()
            var sector = start
            while (sector >= 0 && sector < fat.size && sectors.size <= fat.size) {
                sectors.add(sector)
                sector = fat[sector]
            }
            return sectors
        }

        private fun readSectors(
            sectors: List<Int>,
            size: Int,
        ): ByteArray {
            val result = ByteArray(minOf(size, sectors.size * sectorSize))
            var written = 0
            for (sector in sectors) {
                if (written >= result.size) break
                val offset = sectorOffset(sector)
                val length = minOf(sectorSize, result.size - written, data.size - offset)
                if (length <= 0) break
                data.copyInto(result, written, offset, offset + length)
                written += length
            }
            return result
        }

        private fun readDirectory(start: Int): List<Entry> {
            val sectors = chain(start)
            val directory = readSectors(sectors, sectors.size * sectorSize)
            val result = ArrayList<Entry>()
            var offset = 0
            while (offset + DIRECTORY_ENTRY_SIZE <= directory.size) {
                val nameBytes = (u16(directory, offset + 0x40) - 2).coerceIn(0, 62)
                val name = buildString { for (index in 0 until nameBytes step 2) append(u16(directory, offset + index).toChar()) }
                result.add(Entry(name, u8(directory, offset + 0x42), i32(directory, offset + 0x74), i32(directory, offset + 0x78)))
                offset += DIRECTORY_ENTRY_SIZE
            }
            return result
        }

        private companion object {
            const val HEADER_SIZE = 512
            const val HEADER_DIFAT_ENTRIES = 109
            const val DIRECTORY_ENTRY_SIZE = 128
            const val TYPE_STREAM = 2
            const val TYPE_ROOT = 5
            const val END_OF_CHAIN = -2
            const val FREE_SECTOR = -1
        }
    }

    /** Fluxo "Workbook": registros BIFF (id, tamanho, dados) com os dados globais seguidos de cada aba. */
    private class Workbook(
        private val data: ByteArray,
    ) {
        private val sharedStrings = ArrayList<String>()
        private val formatCodes = HashMap<Int, String>()
        private val cellFormats = ArrayList<Int>()
        private val sheetOffsets = ArrayList<Int>()
        private var date1904 = false

        init {
            readGlobals()
        }

        fun largestSheet(): List<List<String>> {
            val sheets = sheetOffsets.map(::readSheet)
            return sheets.maxByOrNull { sheet -> sheet.sumOf { row -> row.count { it.isNotEmpty() } } }
                ?: throw SpreadsheetFormatException("Planilha XLS sem aba encontrada")
        }

        private fun readGlobals() {
            var offset = 0
            while (offset + 4 <= data.size) {
                val id = u16(data, offset)
                val length = u16(data, offset + 2)
                val body = offset + 4
                if (body + length > data.size) break
                when (id) {
                    RECORD_BOF ->
                        if (length >= 2 && u16(data, body) != BIFF8) {
                            throw SpreadsheetFormatException("Formato XLS anterior ao Excel 97 não é suportado")
                        }
                    RECORD_FILE_PASS -> throw SpreadsheetFormatException("Planilha XLS protegida por senha")
                    RECORD_DATE_MODE -> date1904 = length >= 2 && u16(data, body) == 1
                    RECORD_FORMAT -> if (length >= 5) formatCodes[u16(data, body)] = readString(body + 4, u16(data, body + 2), body + length)
                    RECORD_XF -> cellFormats.add(if (length >= 4) u16(data, body + 2) else 0)
                    RECORD_BOUND_SHEET -> if (length >= 6 && u8(data, body + 5) == SHEET_TYPE_WORKSHEET) sheetOffsets.add(i32(data, body))
                    RECORD_SST -> readSharedStrings(offset)
                    RECORD_EOF -> return
                }
                offset = body + length
            }
        }

        /** Texto de tamanho [length] (em caracteres) que começa pelo byte de opções em [offset]. */
        private fun readString(
            offset: Int,
            length: Int,
            limit: Int,
        ): String {
            if (offset >= limit) return ""
            val wide = u8(data, offset) and 1 != 0
            val builder = StringBuilder(length)
            var position = offset + 1
            repeat(length) {
                if (wide) {
                    if (position + 2 > limit) return builder.toString()
                    builder.append(u16(data, position).toChar())
                    position += 2
                } else {
                    if (position + 1 > limit) return builder.toString()
                    builder.append(u8(data, position).toChar())
                    position += 1
                }
            }
            return builder.toString()
        }

        /**
         * Tabela de textos compartilhados. Ela continua em registros CONTINUE; um texto cortado na
         * fronteira recomeça com um novo byte de opções, que pode trocar a largura dos caracteres.
         */
        private fun readSharedStrings(recordOffset: Int) {
            val segments = ArrayList<IntArray>()
            var offset = recordOffset
            var first = true
            while (offset + 4 <= data.size) {
                val id = u16(data, offset)
                val length = u16(data, offset + 2)
                if (!first && id != RECORD_CONTINUE) break
                if (offset + 4 + length > data.size) break
                segments.add(intArrayOf(offset + 4, offset + 4 + length))
                first = false
                offset += 4 + length
            }
            val reader = SegmentReader(data, segments)
            reader.skip(4)
            val unique = reader.int()
            repeat(unique.coerceAtLeast(0)) {
                if (reader.exhausted()) return
                val length = reader.short()
                val options = reader.byte()
                val richRuns = if (options and 0x08 != 0) reader.short() else 0
                val extendedSize = if (options and 0x04 != 0) reader.int() else 0
                sharedStrings.add(reader.chars(length, wide = options and 1 != 0))
                reader.skip(richRuns * 4 + extendedSize)
            }
        }

        private fun readSheet(start: Int): List<List<String>> {
            val rows = HashMap<Int, MutableMap<Int, String>>()

            fun put(
                row: Int,
                column: Int,
                value: String,
            ) {
                if (value.isNotEmpty()) rows.getOrPut(row) { HashMap() }[column] = value
            }
            var pendingFormula: Pair<Int, Int>? = null
            var offset = start
            var started = false
            while (offset + 4 <= data.size) {
                val id = u16(data, offset)
                val length = u16(data, offset + 2)
                val body = offset + 4
                if (body + length > data.size) break
                when (id) {
                    RECORD_BOF -> started = true
                    RECORD_EOF -> if (started) break
                    RECORD_LABEL_SST ->
                        if (length >= 10) put(u16(data, body), u16(data, body + 2), sharedStrings.getOrElse(i32(data, body + 6)) { "" }.trim())
                    RECORD_NUMBER ->
                        if (length >= 14) put(u16(data, body), u16(data, body + 2), number(f64(data, body + 6), u16(data, body + 4)))
                    RECORD_RK ->
                        if (length >= 10) put(u16(data, body), u16(data, body + 2), number(decodeRk(i32(data, body + 6)), u16(data, body + 4)))
                    RECORD_MUL_RK -> {
                        val row = u16(data, body)
                        val firstColumn = u16(data, body + 2)
                        for (index in 0 until (length - 6) / 6) {
                            val entry = body + 4 + index * 6
                            put(row, firstColumn + index, number(decodeRk(i32(data, entry + 2)), u16(data, entry)))
                        }
                    }
                    RECORD_FORMULA ->
                        if (length >= 14) {
                            val row = u16(data, body)
                            val column = u16(data, body + 2)
                            if (u16(data, body + 12) == 0xFFFF) {
                                // Resultado não numérico: o texto vem no registro STRING seguinte.
                                pendingFormula = if (u8(data, body + 6) == 0) row to column else null
                            } else {
                                put(row, column, number(f64(data, body + 6), u16(data, body + 4)))
                            }
                        }
                    RECORD_STRING -> {
                        val target = pendingFormula
                        if (target != null && length >= 3) put(target.first, target.second, readString(body + 2, u16(data, body), body + length).trim())
                        pendingFormula = null
                    }
                    RECORD_LABEL ->
                        if (length >= 9) put(u16(data, body), u16(data, body + 2), readString(body + 8, u16(data, body + 6), body + length).trim())
                    RECORD_BOOL_ERR ->
                        if (length >= 8 && u8(data, body + 7) == 0) put(u16(data, body), u16(data, body + 2), u8(data, body + 6).toString())
                }
                offset = body + length
            }
            return rows.keys.sorted().map { rowIndex ->
                val cells = rows.getValue(rowIndex)
                val width = cells.keys.max() + 1
                List(width) { column -> cells[column].orEmpty() }
            }
        }

        private fun number(
            value: Double,
            cellFormat: Int,
        ): String {
            val formatId = cellFormats.getOrElse(cellFormat) { 0 }
            val isDate = formatId in BUILTIN_DATE_FORMATS || formatCodes[formatId]?.let(::isDateFormat) == true
            if (isDate) formatDate(value, date1904)?.let { return it }
            return formatNumber(value)
        }
    }

    /** Leitura sequencial sobre os trechos de dados do SST e de seus registros CONTINUE. */
    private class SegmentReader(
        private val data: ByteArray,
        private val segments: List<IntArray>,
    ) {
        private var segment = 0
        private var position = segments.firstOrNull()?.get(0) ?: 0

        fun exhausted(): Boolean {
            advance()
            return segment >= segments.size
        }

        fun byte(): Int {
            advance()
            if (segment >= segments.size) return 0
            return u8(data, position++)
        }

        fun short(): Int = byte() or (byte() shl 8)

        fun int(): Int = short() or (short() shl 16)

        fun skip(count: Int) {
            var remaining = count
            while (remaining > 0) {
                advance()
                if (segment >= segments.size) return
                val step = minOf(remaining, segments[segment][1] - position)
                position += step
                remaining -= step
            }
        }

        fun chars(
            count: Int,
            wide: Boolean,
        ): String {
            val builder = StringBuilder(count)
            var remaining = count
            var isWide = wide
            while (remaining > 0) {
                if (segment >= segments.size) break
                if (position >= segments[segment][1]) {
                    // Texto cortado entre registros: o trecho seguinte começa com um novo byte de opções.
                    segment++
                    if (segment >= segments.size) break
                    position = segments[segment][0]
                    isWide = u8(data, position++) and 1 != 0
                    continue
                }
                val available = segments[segment][1] - position
                if (isWide) {
                    if (available < 2) {
                        position = segments[segment][1]
                        continue
                    }
                    builder.append(u16(data, position).toChar())
                    position += 2
                } else {
                    builder.append(u8(data, position).toChar())
                    position += 1
                }
                remaining--
            }
            return builder.toString()
        }

        private fun advance() {
            while (segment < segments.size && position >= segments[segment][1]) {
                segment++
                if (segment < segments.size) position = segments[segment][0]
            }
        }
    }
}
